'use client'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { ServiceType } from '@/features/cases'

export type OnboardingDocumentKind = 'cas_pdf' | 'broker_xlsx'

export interface PendingOnboardingDocument {
  file: File
  kind: OnboardingDocumentKind
  password?: string
}

/** Step 1 of the Build Portfolio wizard. `phone` is kept here (unlike audit/trades, which have no
 * equivalent) purely to prefill the shared final mobile/slot step's callback-number field — it is
 * never sent as part of `clientDetails` to the backend (see `CreatePublicBuildPortfolioCaseDto`,
 * which takes `mobile` as its own top-level field, separate from `clientDetails`). */
export interface BuildClientDetailsInput {
  name: string
  age: number
  city?: string
  phone: string
  email?: string
}

/** Step 2 — mirrors `BuildPortfolioExpensesDto` exactly (fixed 2dp decimal strings). */
export interface BuildExpensesInput {
  monthlyIncome: string
  rentEmi: string
  utilitiesGroceries: string
  transportLifestyle: string
  otherExpenses: string
}

export type GoalKey = 'wealth' | 'retirement' | 'home' | 'education' | 'marriage'

/** One selected goal from Step 3 — mirrors `CreateGoalDto`; which fields are set depends on `key`. */
export interface BuildGoalInput {
  key: GoalKey
  childName?: string
  targetAge?: number
  presentCost?: string
  monthlyExpense?: string
  years?: number
}

/**
 * Transient wizard state for the public onboarding funnel — everything captured before the real
 * `POST /public/cases` (or `POST /public/cases/build-portfolio`) call fires (at the mobile/slot
 * step, the earliest point every required field is known). Left a plain Zustand singleton, not
 * `createStoreContext`-scoped: every consumer is a pure client component with no SSR touch point,
 * so the per-session-leak risk that factory guards against doesn't apply here.
 */
interface OnboardingState {
  serviceType: ServiceType | null
  setServiceType: (serviceType: ServiceType) => void
  document: PendingOnboardingDocument | null
  setDocument: (document: PendingOnboardingDocument | null) => void
  buildClientDetails: BuildClientDetailsInput | null
  setBuildClientDetails: (v: BuildClientDetailsInput) => void
  buildExpenses: BuildExpensesInput | null
  setBuildExpenses: (v: BuildExpensesInput) => void
  buildGoals: BuildGoalInput[] | null
  setBuildGoals: (v: BuildGoalInput[]) => void
  reset: () => void
}

const PERSIST_KEY = 'nivex-onboarding'

type PersistedState = Pick<
  OnboardingState,
  'serviceType' | 'buildClientDetails' | 'buildExpenses' | 'buildGoals'
>

const EMPTY_STATE = {
  serviceType: null,
  document: null,
  buildClientDetails: null,
  buildExpenses: null,
  buildGoals: null,
} satisfies Pick<
  OnboardingState,
  'serviceType' | 'document' | 'buildClientDetails' | 'buildExpenses' | 'buildGoals'
>

/**
 * Zustand's `persist` middleware rehydrates asynchronously — even for a synchronous storage like
 * `sessionStorage`, its internal `hydrate()` always resolves via a `.then()` (a microtask), never
 * during the store's own creation. Every consumer here reads the store exactly once at first
 * render (RHF's `defaultValues`, `buildPortfolioGoalsView.tsx`'s lazy `useState` initializers) —
 * neither re-syncs itself when the store updates a tick later, so relying on `persist`'s own
 * rehydration meant every field looked reset on a real page refresh even though the data was
 * sitting right there in `sessionStorage`. Reading it synchronously here, before `create()` ever
 * runs, and passing `skipHydration: true` below (skipping `persist`'s own redundant, async
 * rehydration pass entirely) is what actually fixes it — the store's *first* state already has the
 * persisted values, so there's nothing to catch up on later.
 */
function readPersistedState(): Partial<PersistedState> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as { state?: Partial<PersistedState> }
    return parsed.state ?? {}
  } catch {
    return {}
  }
}

/**
 * Persisted to `sessionStorage`, not `localStorage`: this is transient in-progress wizard data
 * (name, age, income, goals — PII-adjacent), so it should live only as long as the tab does, not
 * sit on the device indefinitely. `partialize` deliberately excludes `document` — it holds a raw
 * `File`, which isn't JSON-serializable and would silently rehydrate as garbage; a document upload
 * not surviving a refresh is pre-existing behavior anyway (no browser survives that for a file
 * input), not a regression this introduces.
 */
export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...EMPTY_STATE,
      ...readPersistedState(),
      setServiceType: (serviceType) => set({ serviceType }),
      setDocument: (document) => set({ document }),
      setBuildClientDetails: (buildClientDetails) => set({ buildClientDetails }),
      setBuildExpenses: (buildExpenses) => set({ buildExpenses }),
      setBuildGoals: (buildGoals) => set({ buildGoals }),
      reset: () => set(EMPTY_STATE),
    }),
    {
      name: PERSIST_KEY,
      storage: createJSONStorage(() => sessionStorage),
      skipHydration: true,
      partialize: (state) => ({
        serviceType: state.serviceType,
        buildClientDetails: state.buildClientDetails,
        buildExpenses: state.buildExpenses,
        buildGoals: state.buildGoals,
      }),
    },
  ),
)
